/**
 * Direct-chat delivery helpers — doorbell path + backlog-staleness expiry.
 *
 * ENG-6850 retired the direct-chat one-shot `claude -p` fallback (the T3
 * synchronous-human rail). Delivery is now the doorbell + pull-cursor rail
 * (ENG-5927 / ADR-0020): the manager rings a content-free doorbell, the agent's
 * in-session MCP pulls via the capped `/host/direct-chat/poll` claim, and a
 * message that can't be delivered in-session stays PENDING on the durable queue
 * so it redelivers on the next poll / session recovery. With no fresh `claude -p`
 * process ever spawned, the storm circuit-breaker (`DirectChatSpawnGate`) that
 * used to bound the OOM blast radius is gone — there is nothing left to bound.
 *
 * What remains here is the small, pure contract the manager and the in-session
 * MCP share:
 *   - `directChatDoorbellPath` — the file the manager touches to wake the MCP.
 *   - `isDirectChatMessageExpired` / `directChatMaxAgeMs` — backlog-staleness:
 *     a message that replayed long after the user gave up is drained with a
 *     terminal "[Expired]" notice (honest, visible degradation) instead of
 *     redelivering forever.
 *
 * Pure module — no I/O, no process spawns, no HTTP — so the manager↔MCP contract
 * stays exercisable in vitest without the async poll-loop machinery around it.
 */

import { join } from 'node:path';

/** Backlog messages older than this are drained without delivery (30 min). */
export const DEFAULT_DIRECT_CHAT_MAX_AGE_MS = 30 * 60_000;

/**
 * Backlog-message expiry window in ms, read from `AGT_DIRECT_CHAT_MAX_AGE_MS`.
 * `0` disables expiry. A negative or non-numeric value falls back to the
 * default; `0` is honored verbatim.
 */
export function directChatMaxAgeMs(): number {
  const raw = parseInt(process.env['AGT_DIRECT_CHAT_MAX_AGE_MS'] ?? '', 10);
  return Number.isFinite(raw) && raw >= 0 ? raw : DEFAULT_DIRECT_CHAT_MAX_AGE_MS;
}

/**
 * Path of the content-free doorbell file the manager touches to wake an agent's
 * in-session direct-chat MCP. Keyed on the agent UUID — NOT the code_name — to
 * match the MCP's `DIRECT_CHAT_AGENT_DIR` (`~/.augmented/<AGT_AGENT_ID>/`); the
 * direct-chat MCP scopes by `AGT_AGENT_ID`, unlike the Slack/Telegram servers.
 * Pure path builder (no I/O) so the manager↔MCP contract is unit-testable.
 */
export function directChatDoorbellPath(agentId: string, home: string): string {
  return join(home, '.augmented', agentId, 'direct-chat-doorbell');
}

/**
 * Whether a direct-chat message is stale enough to drain with a terminal notice
 * instead of redelivering. Fail-safe: a disabled window, a missing timestamp
 * (Realtime messages carry none — they are by definition fresh), or an
 * unparseable timestamp all return `false` (never expire). Only the poll/backlog
 * path carries a `created_at`, which is exactly the replay path.
 */
export function isDirectChatMessageExpired(
  createdAt: string | undefined,
  nowMs: number,
  maxAgeMs: number,
): boolean {
  if (!maxAgeMs || maxAgeMs <= 0) return false;
  if (!createdAt) return false;
  const created = Date.parse(createdAt);
  if (!Number.isFinite(created)) return false;
  return nowMs - created > maxAgeMs;
}
